import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { HiFinalCta, HI_MARKETS } from "./HindiHome";
import { METROS, HI_METROS } from "../data/metros";

const HI_PRIMARY = [
    {
        name: "नर्मदापुरम",
        text: "हमारा होम मार्केट। नर्मदापुरम भर के बिज़नेस के लिए ब्रांड स्ट्रैटेजी, रिटेल और ट्रेड मार्केटिंग, डिजिटल ग्रोथ, कस्टमर एक्विज़िशन और सेल्स-फ़ोकस्ड मार्केटिंग।",
    },
    {
        name: "इतारसी",
        text: "मध्य प्रदेश का कमर्शियल चौराहा। हम इतारसी के बिज़नेस की लोकेशन और ट्रेडिंग ताक़त को व्यवस्थित, मापी जा सकने वाली ग्रोथ में बदलते हैं।",
    },
];

const METRO_HI_NAMES = ["दिल्ली", "भोपाल", "इंदौर", "जबलपुर"];

const HindiMarkets = () => {
    useEffect(() => {
        document.title = "हमारे बाज़ार | दिल्ली, भोपाल, नर्मदापुरम, इतारसी — ALBL Communications";
    }, []);

    return (
        <main lang="hi">
            <section className="bg-[#0D131F] hero-grid text-white pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <Eyebrow dark>बाज़ार</Eyebrow>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] mt-5 max-w-4xl" data-testid="hi-markets-hero-heading">
                            लोकल से शुरुआत। <span className="text-[#C5A059]">बड़ी सोच।</span>
                        </h1>
                        <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">
                            ALBL नर्मदापुरम, इतारसी और आसपास के मध्य प्रदेश के बाज़ारों के साथ-साथ दिल्ली, भोपाल, इंदौर और जबलपुर के बिज़नेस के साथ काम करती है।
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow>होम मार्केट</Eyebrow>
                    </Reveal>
                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        {HI_PRIMARY.map((m, i) => (
                            <Reveal key={m.name} delay={i * 80}>
                                <div className="border border-slate-200 bg-[#F8FAFC] p-8 sm:p-12" data-testid={`hi-market-card-${i === 0 ? "narmadapuram" : "itarsi"}`}>
                                    <MapPin className="h-6 w-6 text-[#C5A059]" />
                                    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-4">
                                        {m.name}
                                    </h2>
                                    <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">{m.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={100}>
                        <Eyebrow>
                            <span className="block mt-20">मेट्रो बाज़ार</span>
                        </Eyebrow>
                        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {METROS.map((m) => (
                                <Link
                                    key={m.slug}
                                    to={`/hi/markets/${m.slug}`}
                                    data-testid={`hi-market-metro-card-${m.slug}`}
                                    className="group border border-slate-200 bg-white p-7 hover:border-[#0D131F] transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display text-xl font-bold text-slate-900 group-hover:text-[#1E3A8A] transition-colors">
                                            {HI_METROS[m.slug] ? m.city : m.city}
                                        </h3>
                                        <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                        {(HI_METROS[m.slug] || m).heroSub}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={150}>
                        <Eyebrow>
                            <span className="block mt-20">सर्विस एरिया</span>
                        </Eyebrow>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {HI_MARKETS.filter((m) => !METRO_HI_NAMES.includes(m)).map((m) => (
                                <span
                                    key={m}
                                    className="px-5 py-3 text-sm tracking-wide border border-slate-300 text-slate-700"
                                    data-testid={`hi-market-area-${m}`}
                                >
                                    {m}
                                </span>
                            ))}
                        </div>
                        <p className="mt-10 text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl">
                            चाहे आपका बिज़नेस एक मोहल्ले को serve करता हो या पूरे इलाके में डिस्ट्रीब्यूशन — हम मार्केटिंग उसी geography के अनुसार बनाते हैं जहाँ आप सच में बेचते हैं, enquiries, ग्राहकों और सेल्स से मापी हुई।
                        </p>
                        <Link
                            to="/hi/contact?intent=audit"
                            data-testid="hi-markets-cta-audit-button"
                            className="btn-sharp mt-10 inline-flex items-center gap-2 bg-[#0D131F] text-white text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                        >
                            ग्रोथ ऑडिट लें <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <HiFinalCta />
        </main>
    );
};

export default HindiMarkets;
